/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Form/EditFieldModal.tsx.
 */

// src/pages/Admin/Form/EditFieldModal.tsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Box,
} from "@mui/material";
import { getAllDropdowns, getUnitProperties } from "../../../../services/FormService";
import { FormField } from "../../../../interfaces/formInterfaces";
import {
  getAdminFormFieldById,
  getFieldTypes,
  getTextInputTypes,
  type FieldTypeOption,
  type TextInputTypeOption,
} from "./FormService";
import {
  appendTextValidationPayload,
  textValidationFromField,
  validateTextFieldInputConstraints,
} from "../../../../utils/adminFormFieldValidation";

// ---------- Interfaces ----------
interface SubSection {
  id: number;
  name: string;
}

interface Dropdown {
  id: number;
  name: string;
}

interface EditFieldModalProps {
  field: FormField;
  subSections?: SubSection[];
  onClose: () => void;
  onSave: (fieldId: number, data: any) => void;
}

function formStateFromField(f: FormField) {
  return {
    label: f.label || "",
    name: f.name || "",
    type: f.type || "TEXT",
    placeholder: f.placeholder || "",
    infoText: f.infoText || "",
    inputType: f.inputType || "",
    ...textValidationFromField(f),
    isMandatory: f.isMandatory ?? true,
    isEnabled: f.isEnabled ?? true,
    isEditable: f.isEditable ?? true,
    order: f.order || 1,
    formSubSectionId: f.formSubSectionId?.toString() || "",
    dropdownId: f.dropdownId?.toString() || "",
    unitPropertyId: f.unitPropertyId?.toString() || "",
  };
}

// ---------- Component ----------
const EditFieldModal: React.FC<EditFieldModalProps> = ({
  field,
  subSections = [],
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState(() => formStateFromField(field));

  const [dropdowns, setDropdowns] = useState<Dropdown[]>([]);
  const [unitProperties, setUnitProperties] = useState<
    { id: number; name: string; displayName?: string }[]
  >([]);
  const [textInputTypes, setTextInputTypes] = useState<TextInputTypeOption[]>([
    { value: "", label: "Default (text)" },
  ]);
  const [fieldTypeOptions, setFieldTypeOptions] = useState<FieldTypeOption[]>(
    []
  );

  // ✅ Hydrate from GET /forms/admin/field/:id (dropdown options, unitProperty, section summaries)
  useEffect(() => {
    let cancelled = false;
    getAdminFormFieldById(field.id).then((full) => {
      if (cancelled || !full) return;
      setForm(formStateFromField(full));
    });
    return () => {
      cancelled = true;
    };
  }, [field.id]);

  // ✅ Load field types, dropdowns, unit properties, TEXT input types
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ft, dropdownData, units, tit] = await Promise.all([
          getFieldTypes(),
          getAllDropdowns(),
          getUnitProperties(),
          getTextInputTypes(),
        ]);
        setFieldTypeOptions(ft);
        if (Array.isArray(dropdownData)) setDropdowns(dropdownData);
        setUnitProperties(units);
        setTextInputTypes(tit);
      } catch (err) {
        console.error("❌ Failed to load form builder helpers:", err);
      }
    };
    fetchData();
  }, []);

  // ✅ Save Handler
  const handleSave = () => {
    if (!form.label || !form.name || !form.type) {
      alert("Please fill all required fields.");
      return;
    }

    if (form.type === "UNIT" && !String(form.unitPropertyId).trim()) {
      alert("Please select a unit property for Unit fields.");
      return;
    }

    if (form.type === "TEXT") {
      const textErr = validateTextFieldInputConstraints(form);
      if (textErr) {
        alert(textErr);
        return;
      }
    }

    const payload: Record<string, unknown> = {
      label: form.label,
      name: form.name,
      type: form.type,
      inputType: form.inputType || undefined,
      placeholder: form.placeholder || undefined,
      infoText: form.infoText.trim() || null,
      isMandatory: form.isMandatory,
      isEnabled: form.isEnabled,
      isEditable: form.isEditable,
      order: form.order,
      formSubSectionId: form.formSubSectionId
        ? Number(form.formSubSectionId)
        : undefined,
      unitPropertyId:
        form.type === "UNIT" && form.unitPropertyId
          ? Number(form.unitPropertyId)
          : undefined,
    };

    if (form.type === "DROPDOWN" && form.dropdownId) {
      payload.dropdownId = Number(form.dropdownId);
    } else if (form.type === "RAW_MATERIAL_QUALITY") {
      payload.dropdownId = form.dropdownId ? Number(form.dropdownId) : null;
    } else if (
      form.type === "MANUFACTURING_PROCESS" ||
      form.type === "EXTRACTION_PROCESS" ||
      form.type === "PLANT_CAPACITY"
    ) {
      payload.dropdownId = null;
    }

    appendTextValidationPayload(payload, form.type, {
      defaultValue: form.defaultValue,
      textMaxLength: form.textMaxLength,
      numberMin: form.numberMin,
      numberMax: form.numberMax,
      maxDecimalPlaces: form.maxDecimalPlaces,
    });

    onSave(field.id, payload);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Field</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {/* Label */}
          <TextField
            label="Field Label *"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            fullWidth
          />

          {/* Name */}
          <TextField
            label="Field Name (unique) *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            disabled // Name should not be editable after creation
            helperText="Field name cannot be changed after creation"
          />

          {/* Type — GET /forms/admin/field-types */}
          <FormControl fullWidth>
            <InputLabel>Field Type</InputLabel>
            <Select
              value={form.type}
              label="Field Type"
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {fieldTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
              {form.type && !fieldTypeOptions.some((o) => o.value === form.type) && (
                <MenuItem value={form.type}>{form.type}</MenuItem>
              )}
            </Select>
          </FormControl>

          {form.type === "EXTRACTION_PROCESS" && (
            <Box sx={{ typography: "body2", color: "text.secondary" }}>
              Options load per material on the A1 form; no CMS dropdown is linked.
            </Box>
          )}

          {form.type === "MANUFACTURING_PROCESS" && (
            <Box sx={{ typography: "body2", color: "text.secondary" }}>
              Options come from manufacturing processes for the finished material when forms are loaded
              with <code>materialId</code>. No CMS dropdown is linked.
            </Box>
          )}

          {form.type === "PLANT_CAPACITY" && (
            <Box sx={{ typography: "body2", color: "text.secondary" }}>
              Options come from plant capacity bands for the finished material when forms are loaded with{" "}
              <code>materialId</code>. No CMS dropdown is linked.
            </Box>
          )}

          {form.type === "RAW_MATERIAL_QUALITY" && (
            <Box sx={{ typography: "body2", color: "text.secondary" }}>
              Link an optional CMS dropdown below for a single select (e.g. mix design / material quality).
              Leave it unset to use the per-material attribute catalog on the contributor form instead.
            </Box>
          )}

          {/* Dropdown Selector (if DROPDOWN type) */}
          {form.type === "DROPDOWN" && (
            <FormControl fullWidth>
              <InputLabel>Select Dropdown</InputLabel>
              <Select
                value={form.dropdownId}
                label="Select Dropdown"
                onChange={(e) =>
                  setForm({ ...form, dropdownId: e.target.value })
                }
              >
                <MenuItem value="">(None)</MenuItem>
                {dropdowns.map((d) => (
                  <MenuItem key={d.id} value={d.id.toString()}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {form.type === "RAW_MATERIAL_QUALITY" && (
            <FormControl fullWidth>
              <InputLabel>Optional CMS dropdown</InputLabel>
              <Select
                value={form.dropdownId}
                label="Optional CMS dropdown"
                onChange={(e) =>
                  setForm({ ...form, dropdownId: e.target.value })
                }
              >
                <MenuItem value="">
                  <em>Catalog attributes only (no CMS list)</em>
                </MenuItem>
                {dropdowns.map((d) => (
                  <MenuItem key={d.id} value={d.id.toString()}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* UnitProperty Selector (if UNIT type) */}
          {form.type === "UNIT" && (
            <FormControl fullWidth>
              <InputLabel id="edit-field-unit-property-label">
                Unit property *
              </InputLabel>
              <Select
                labelId="edit-field-unit-property-label"
                value={form.unitPropertyId}
                label="Unit property *"
                onChange={(e) =>
                  setForm({ ...form, unitPropertyId: e.target.value })
                }
              >
                <MenuItem value="">
                  <em>Select a property…</em>
                </MenuItem>
                {unitProperties.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.displayName || u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Input type — GET /forms/admin/text-input-types */}
          {form.type === "TEXT" && (
            <>
              <FormControl fullWidth>
                <InputLabel>Input type</InputLabel>
                <Select
                  value={form.inputType}
                  label="Input type"
                  onChange={(e) =>
                    setForm({ ...form, inputType: e.target.value })
                  }
                >
                  {textInputTypes.map((opt) => (
                    <MenuItem
                      key={opt.value === "" ? "__default__" : opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ typography: "body2", color: "text.secondary" }}>
                Text / default: set <strong>max length</strong>. Integer or decimal: set{" "}
                <strong>minimum</strong> and <strong>maximum</strong> (required).
              </Box>
            </>
          )}

          {form.type === "TEXT" && (
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Default value (optional)"
                value={form.defaultValue}
                onChange={(e) =>
                  setForm({ ...form, defaultValue: e.target.value })
                }
                fullWidth
              />
              {(form.inputType === "" || form.inputType === "text") && (
                <TextField
                  label="Max length (characters) *"
                  type="number"
                  required
                  value={form.textMaxLength}
                  onChange={(e) =>
                    setForm({ ...form, textMaxLength: e.target.value })
                  }
                  fullWidth
                  inputProps={{ min: 0 }}
                  helperText="Required for text input."
                />
              )}
              {(form.inputType === "number" ||
                form.inputType === "double" ||
                form.inputType === "integer") && (
                <>
                  <TextField
                    label="Minimum *"
                    required
                    value={form.numberMin}
                    onChange={(e) =>
                      setForm({ ...form, numberMin: e.target.value })
                    }
                    fullWidth
                    inputProps={{ inputMode: "decimal" }}
                    helperText="Required for integer or decimal input."
                  />
                  <TextField
                    label="Maximum *"
                    required
                    value={form.numberMax}
                    onChange={(e) =>
                      setForm({ ...form, numberMax: e.target.value })
                    }
                    fullWidth
                    inputProps={{ inputMode: "decimal" }}
                    helperText="Required for integer or decimal input."
                  />
                  {form.inputType === "double" && (
                    <TextField
                      label="Max decimal places (optional)"
                      type="number"
                      value={form.maxDecimalPlaces}
                      onChange={(e) =>
                        setForm({ ...form, maxDecimalPlaces: e.target.value })
                      }
                      fullWidth
                      inputProps={{ min: 0 }}
                    />
                  )}
                </>
              )}
            </Box>
          )}

          {/* Placeholder */}
          <TextField
            label="Placeholder (optional)"
            value={form.placeholder}
            onChange={(e) => setForm({ ...form, placeholder: e.target.value })}
            fullWidth
          />

          {/* Tooltip */}
          <TextField
            label="Tooltip (optional)"
            value={form.infoText}
            onChange={(e) => setForm({ ...form, infoText: e.target.value })}
            fullWidth
            multiline
            minRows={2}
            helperText="Shown as an info icon next to the field label on contributor forms."
          />

          {/* Subsection assignment */}
          {subSections.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Assign to Subsection</InputLabel>
              <Select
                value={form.formSubSectionId}
                label="Assign to Subsection"
                onChange={(e) =>
                  setForm({ ...form, formSubSectionId: e.target.value })
                }
              >
                <MenuItem value="">(None)</MenuItem>
                {subSections.map((s) => (
                  <MenuItem key={s.id} value={s.id.toString()}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Boolean Flags */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isMandatory}
                  onChange={(e) =>
                    setForm({ ...form, isMandatory: e.target.checked })
                  }
                />
              }
              label="Mandatory"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isEnabled}
                  onChange={(e) =>
                    setForm({ ...form, isEnabled: e.target.checked })
                  }
                />
              }
              label="Enabled"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isEditable}
                  onChange={(e) =>
                    setForm({ ...form, isEditable: e.target.checked })
                  }
                />
              }
              label="Editable"
            />
          </Box>

          {/* Order */}
          <TextField
            type="number"
            label="Order"
            value={form.order}
            onChange={(e) =>
              setForm({ ...form, order: Number(e.target.value) })
            }
            fullWidth
            inputProps={{ min: 1 }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** React component — Edit Field Modal. */
export default EditFieldModal;
